import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  Avatar,
  Divider,
  CircularProgress,
  Link,
  Tabs,
  Tab,
  Button,
  DialogActions,
  Snackbar,
  DialogTitle,
  Dialog,
  DialogContent,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import Sidebar from '../../components/sidebars/Sidebar _inventory';
import TopNav from '../../components/top_bars/TopNav';
import StatisticsCards from '../../components/top_bars/StatisticsCard';

const yesNo = (val) => (val === '1' ? 'Yes' : 'No');

const StaffProfileView = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [securityUploads, setSecurityUploads] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  useEffect(() => {
    const phoneNumber = localStorage.getItem('phoneNumber');
    const storedUser = localStorage.getItem('userProfile');
    
    if (storedUser) {
      try {
        const userProfile = JSON.parse(storedUser);
        setUserRole(userProfile.user_type);
        setUserName(userProfile.full_name || `${userProfile.surname} ${userProfile.other_names}`);
      } catch (e) {
        console.error('Error parsing user profile:', e);
      }
    }

    const fetchProfile = async () => {
      try {
        const [profileRes, extraRes, uploadsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/get_full_profile.php?phone=${phoneNumber}`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/get_educationExperienceLastemp.php?phone=${phoneNumber}`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/get_uploads.php?phone=${phoneNumber}`),
        ]);

        const profileJson = await profileRes.json();
        const extraJson = await extraRes.json();
        const uploadsJson = await uploadsRes.json();

        if (profileJson.success) {
          setProfileData({
            ...profileJson.data,
            education: extraJson?.data?.education || [],
            experience: extraJson?.data?.experience || [],
            lastEmployer: extraJson?.data?.lastEmployer || [],
          });
        } else {
          setError('Profile information could not be loaded');
        }

        if (uploadsJson?.data?.length > 0) {
          setSecurityUploads(uploadsJson.data[0]);
        }

      } catch (err) {
        console.error(err);
        setError('Network error. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (phoneNumber) {
      fetchProfile();
    } else {
      setError('No phone number found. Please contact HR.');
      setLoading(false);
    }
  }, []);

  const handleCertification = async () => {
    setConfirmOpen(false);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/completion_state_.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: localStorage.getItem('phoneNumber') }),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({ open: true, message: result.message || 'Documentation certified successfully!', severity: 'success' });
      } else {
        const msg = result.message || 'Submission failed';
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Network error. Please try again.', severity: 'error' });
    }
  };

  const renderField = (label, value) => (
    <Typography variant="body1" sx={{ mb: 1 }}>
      <strong>{label}:</strong> {value || 'Not provided'}
    </Typography>
  );

  const renderSection = (title, children, showEditButton = false) => (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
      <CardHeader 
        title={title} 
        titleTypographyProps={{ 
          variant: 'h6',
          sx: { 
            color: 'primary.main',
            fontWeight: 600,
          }
        }}
        action={
          showEditButton && (
            <Tooltip title="Contact HR to update this information">
              <IconButton size="small" disabled>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        }
      />
      <Divider />
      <CardContent>
        {children}
      </CardContent>
    </Card>
  );

  const renderPersonalInfo = () => {
    const { personalInfo, additionalInfo, profilePhoto } = profileData;
    const fullName = `${personalInfo?.surname || ''} ${personalInfo?.other_names || ''}`;

    return (
      <>
        {/* Welcome Banner for Staff */}
        {userRole !== 'hr' && userRole !== 'admin' && (
          <Paper 
            sx={{ 
              p: 2, 
              mb: 3, 
              bgcolor: 'primary.light', 
              color: 'primary.contrastText',
              borderRadius: 2
            }}
          >
            <Typography variant="body2">
              Welcome back, {userName || fullName}! Here's your complete profile information.
              To update any details, please contact the HR department.
            </Typography>
          </Paper>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
              <Avatar
                alt={fullName}
                src={`${process.env.REACT_APP_API_BASE_URL}/${profilePhoto}`}
                sx={{
                  width: 160,
                  height: 160,
                  border: '3px solid',
                  borderColor: 'primary.main',
                  mb: 2,
                }}
              />
              <Typography variant="h6" align="center" gutterBottom>
                {fullName}
              </Typography>
              <Chip 
                label={personalInfo?.department || 'Department Not Assigned'} 
                color="primary" 
                variant="outlined" 
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                {personalInfo?.email || 'Email not provided'}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Role: <strong>{userRole?.toUpperCase() || 'Staff'}</strong>
              </Typography>
              {personalInfo?.status === 'Active' && (
                <Chip 
                  label="Active Employee" 
                  color="success" 
                  size="small" 
                  icon={<CheckCircleIcon />}
                  sx={{ mt: 2 }}
                />
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {renderSection('Basic Information', (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {renderField('Phone Number', personalInfo?.phone)}
                  {renderField('Date of Birth', personalInfo?.date_of_birth)}
                  {renderField('Marital Status', personalInfo?.marital_status)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderField('State of Origin', personalInfo?.state_of_origin)}
                  {renderField('LGA', personalInfo?.lga)}
                  {renderField('Country', personalInfo?.country)}
                </Grid>
                <Grid item xs={12}>
                  {renderField('Residential Address', personalInfo?.residential_address)}
                  {renderField('Permanent Address', personalInfo?.hometown_permanent_address)}
                </Grid>
              </Grid>
            ))}

            {additionalInfo && renderSection('Next of Kin Information', (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {renderField('Next of Kin', additionalInfo.next_of_kin_name)}
                  {renderField('Relationship', additionalInfo.relationship)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderField('Next of Kin Contact', additionalInfo.next_of_kin_contact)}
                  {renderField('Health Status', additionalInfo.health_status || 'Not specified')}
                </Grid>
                <Grid item xs={12}>
                  {renderField('Next of Kin Address', additionalInfo.next_of_kin_address)}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </>
    );
  };

  const renderEducation = () => {
    const { education } = profileData;
    
    if (!education?.length) {
      return (
        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No education information found.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please contact HR to update your education details.
          </Typography>
        </Card>
      );
    }

    return renderSection('Education Background', (
      <Grid container spacing={2}>
        {education.map((edu, idx) => (
          <React.Fragment key={idx}>
            <Grid item xs={12} sm={6}>
              {renderField('Primary School', edu.primary_school_name || 'Not provided')}
              {renderField('Primary School Address', edu.primary_school_address || 'Not provided')}
              {renderField('Secondary School', edu.secondary_school_name || 'Not provided')}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderField('Secondary School Address', edu.secondary_school_address || 'Not provided')}
              {renderField('Tertiary School', edu.tertiary_school_name || 'Not provided')}
              {renderField('Tertiary School Address', edu.tertiary_school_address || 'Not provided')}
            </Grid>
            {idx < education.length - 1 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
            )}
          </React.Fragment>
        ))}
      </Grid>
    ));
  };

  const renderExperience = () => {
    const { experience, lastEmployer } = profileData;

    return (
      <>
        {experience?.length > 0 ? renderSection('Work Experience', (
          <Grid container spacing={2}>
            {experience.map((exp, idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={12} sm={6}>
                  {renderField('Role/Experience Type', exp.experience_type)}
                  {renderField('Years of Experience', exp.years_of_experience)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderField('Salary Expectation', exp.salary_expectation ? `₦${parseFloat(exp.salary_expectation).toLocaleString()}` : 'Not specified')}
                </Grid>
                <Grid item xs={12}>
                  {renderField('Last Workplace', exp.last_workplace_one)}
                  {exp.last_workplace_two && renderField('Previous Workplace', exp.last_workplace_two)}
                </Grid>
                <Grid item xs={12}>
                  {renderField('Self Evaluation', exp.self_evaluation || 'Not provided')}
                </Grid>
                {idx < experience.length - 1 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>
        )) : (
          <Card variant="outlined" sx={{ mb: 3, p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No work experience information found.</Typography>
          </Card>
        )}

        {lastEmployer?.length > 0 && renderSection('Previous Employment History', (
          <Grid container spacing={2}>
            {lastEmployer.map((emp, idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={12} sm={6}>
                  {renderField('Previous Employer', emp.previous_employer)}
                  {renderField('Job Title', emp.job_title)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderField('Employment Period', `${emp.employment_start_date || 'N/A'} - ${emp.employment_end_date || 'Present'}`)}
                  {renderField('Reason for Leaving', emp.reason_for_leaving || 'Not specified')}
                </Grid>
                {idx < lastEmployer.length - 1 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>
        ))}
      </>
    );
  };

  const renderSecurity = () => {
    if (!securityUploads) {
      return (
        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No document uploads found.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please ensure you've submitted all required documents to HR.
          </Typography>
        </Card>
      );
    }

    return renderSection('Security & Documents', (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderField('Previous Conviction Declaration', yesNo(securityUploads.convicted))}
          {renderField('Security Declaration Signed', yesNo(securityUploads.security_declaration))}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Uploaded Documents:
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(securityUploads).map(([key, value]) => {
              if (key.endsWith('_upload') && value) {
                const labelMap = {
                  guarantor_upload: 'Guarantor Form',
                  staff_reference_upload: 'Staff Reference',
                  cv_upload: 'Curriculum Vitae',
                  academic_result_upload: 'Academic Results',
                  id_upload: 'Identification Document',
                  staff_signature_upload: 'Staff Signature',
                };
                const label = labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {label}
                        </Typography>
                        <Tooltip title="View Document">
                          <IconButton
                            size="small"
                            component={Link}
                            href={`${process.env.REACT_APP_API_BASE_URL}/${value}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Card>
                  </Grid>
                );
              }
              return null;
            })}
          </Grid>
        </Grid>
      </Grid>
    ));
  };

  const renderCertification = () => {
    // Only show certification tab for HR/Admin users or if not already certified
    const canCertify = userRole === 'hr' || userRole === 'admin' || userRole === 'hr_staff';
    
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Card sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Documentation Review Status
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            {canCertify 
              ? "Confirm that all staff documentation has been reviewed and is complete."
              : "Your documentation is pending HR review. You'll be notified once certified."}
          </Typography>
          
          {canCertify ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => setConfirmOpen(true)}
              startIcon={<CheckCircleIcon />}
            >
              Certify Complete Documentation
            </Button>
          ) : (
            <Chip 
              label="Awaiting HR Certification" 
              color="warning" 
              variant="outlined"
              sx={{ fontSize: '1rem', py: 2, px: 1 }}
            />
          )}
        </Card>
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0: return renderPersonalInfo();
      case 1: return renderEducation();
      case 2: return renderExperience();
      case 3: return renderSecurity();
      case 4: return renderCertification();
      default: return null;
    }
  };

  const renderProfileInfo = () => {
    if (loading) return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
    
    if (error) return (
      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#fff3e0' }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Please contact HR if this issue persists.
        </Typography>
      </Paper>
    );
    
    if (!profileData) return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">No profile data found.</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Please ensure your profile information has been submitted.
        </Typography>
      </Paper>
    );

    return (
      <>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Personal Info" />
          <Tab label="Education" />
          <Tab label="Experience" />
          <Tab label="Documents" />
          {(userRole === 'hr' || userRole === 'admin' || userRole === 'hr_staff') && (
            <Tab label="Certification" />
          )}
        </Tabs>
        
        {renderTabContent()}
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <Box
        sx={{
          flexGrow: 1,
          p: isMobile ? 2 : 3,
          // CORRECTED SPACING - matching the first component's logic
          ml: isMobile ? 0 : sidebarOpen ? '240px' : '60px', // Changed from 280px/80px to 240px/60px
          transition: 'margin-left 0.2s ease-in-out',
          width: 'calc(100% - 60px)',
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
            window.location.href = '/login';
          }}
          onNotificationClick={() => {
            setSnackbar({ open: true, message: 'No new notifications', severity: 'info' });
          }}
          toggleSidebar={toggleSidebar}
        />
        
        {/* Page Title */}
        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h5" fontWeight="600" color="text.primary">
            My Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your personal information, employment history, and uploaded documents
          </Typography>
        </Box>

        {/* Optional: Show statistics cards only for HR/Admin */}
        {(userRole === 'hr' || userRole === 'admin') && (
          <Box sx={{ mt: 1, mb: 3 }}>
            <StatisticsCards />
          </Box>
        )}

        {renderProfileInfo()}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Certification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to certify that all documentation has been reviewed and is complete?
            This action will mark the staff member's profile as certified.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleCertification} variant="contained" color="primary">
            Yes, Certify
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffProfileView;